import { Component, Input, OnInit } from '@angular/core';
import { Collaboration, Company } from '../../../models';
import { BaseComponent } from '../../general/base/base.component';
import {
  IAdvertiserCollaborationsToExcel,
  IAgencyCollaborationsToExcel,
  IPrimengSelectButtonOptions,
} from '../../../shared/interfaces';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../../../shared/services';
import { ConfirmationService, MessageService } from 'primeng/api';
import { UtilService } from '../../../shared/services/util.service';
import { Router } from '@angular/router';
import { Workbook } from 'exceljs';
import capitalize from 'lodash.capitalize';

@Component({
  selector: 'app-collaborations-company-table',
  templateUrl: './collaborations-company-table.component.html',
  styleUrls: ['./collaborations-company-table.component.scss'],
})
export class CollaborationsCompanyTableComponent extends BaseComponent implements OnInit {
  @Input() company: Company | undefined = undefined;
  @Input() mode: 'agency' | 'advertiser' = 'agency';

  public isLoading = true;

  public collaborations: Collaboration[] = [];

  public filteredCollaborations: Collaboration[] = [];

  enabledOptions: IPrimengSelectButtonOptions<'current' | 'all'>[] = [
    { label: 'Current', value: 'current' },
    { label: 'All', value: 'all' },
  ];
  selectedEnabledOptionValue = 'current';
  selectedSearchText = '';
  searchText$: BehaviorSubject<string> = new BehaviorSubject<string>(this.selectedSearchText);

  constructor(
    private readonly apiService: ApiService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly utilService: UtilService,
    private readonly router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.searchText$.subscribe(() => {
      this.selectedEnabledOptionValueChanged();
      this.filteredCollaborations = this.filteredCollaborations.filter(
        (c) =>
          c.campaign?.toLowerCase().includes(this.selectedSearchText.toLowerCase()) ||
          (this.mode === 'agency'
            ? c.advertiser?.name?.toLowerCase().includes(this.selectedSearchText.toLowerCase())
            : c.agency?.name?.toLowerCase().includes(this.selectedSearchText.toLowerCase())) ||
          c.getBrandNames().toLowerCase().includes(this.selectedSearchText.toLowerCase()) ||
          c.getServiceNames().toLowerCase().includes(this.selectedSearchText.toLowerCase()),
      );
    });

    this.refresh();
  }

  public refresh() {
    this.collaborations = [];
    this.filteredCollaborations = [];

    if (this.company) {
      this.isLoading = true;
      this.apiService.companies.getCollaborations(this.company.id, this.mode).subscribe(
        (collaborations: Collaboration[]) => {
          this.setCollaborations(collaborations);
        },
        () => {
          this.showErrorOnGetCollaborations();
        },
      );
    }
  }

  private setCollaborations(collaborations: Collaboration[]) {
    collaborations.forEach((c) => {
      this.collaborations.push(new Collaboration(c));
    });
    this.filteredCollaborations = this.collaborations.filter((c) => c.isActive);
    this.isLoading = false;
  }

  private showErrorOnGetCollaborations() {
    this.isLoading = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'We were unable to load the collaborations. Please try again later.',
    });
  }

  selectedEnabledOptionValueChanged() {
    if (this.selectedEnabledOptionValue == 'current') {
      this.filteredCollaborations = this.collaborations.filter((c) => c.isActive);
    } else {
      this.filteredCollaborations = this.collaborations;
    }
  }

  searchTextChanged() {
    this.searchText$.next(this.selectedSearchText);
  }

  async exportAgencyCollaborationsToExcel() {
    const { columns, rows } = this.getCollaborationsForExcel();
    const workbook = new Workbook();
    const workSheet = workbook.addWorksheet(`Client & Services for company ${this.company?.name}`);
    workSheet.columns = columns;
    workSheet.insertRow(1, null);
    workSheet.mergeCells('A1', 'G1');
    const titleRow = workSheet.getCell('A1');
    titleRow.value = 'CRM';
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    workSheet.addRows(rows);
    const headerRow = workSheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
    const buffer = (await workbook.xlsx.writeBuffer()) as unknown as Buffer;
    this.utilService.saveAsExcelFile(
      buffer,
      `Clients-and-services-for-company-${this.company?.name.replaceAll(' ', '_')}`,
    );
  }

  async exportAdvertiserCollaborationsToExcel() {
    const { columns, rows } = this.getCollaborationsForExcel<'advertiser'>();
    const workbook = new Workbook();
    const workSheet = workbook.addWorksheet(`Received services for company ${this.company?.name}`);
    workSheet.columns = columns;
    workSheet.insertRow(1, null);
    workSheet.mergeCells('A1', 'H1');
    const titleRow = workSheet.getCell('A1');
    titleRow.value = 'CRM';
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    workSheet.addRows(rows);
    const headerRow = workSheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
    const buffer = (await workbook.xlsx.writeBuffer()) as unknown as Buffer;
    this.utilService.saveAsExcelFile(
      buffer,
      `Received-services-for-company-${this.company?.name.replaceAll(' ', '_')}`,
    );
  }

  private getCollaborationsForExcel<T extends 'agency' | 'advertiser' = 'agency'>(): {
    columns: { header: string; key: string; width: number }[];
    rows: T extends 'agency' ? IAgencyCollaborationsToExcel[] : IAdvertiserCollaborationsToExcel[];
  } {
    const rows = this.filteredCollaborations.map((collaboration) =>
      this.mode === 'agency'
        ? collaboration.agencyCollaborationForExcel()
        : collaboration.advertiserCollaborationForExcel(),
    );
    const keys = Object.keys(rows[0]);
    const columns = keys.map((key) => ({ header: capitalize(key), key, width: 30 }));
    return { columns, rows };
  }

  goToOfficeDetailsPage(event: Event, collaboration: Collaboration, type: 'agency' | 'advertiser') {
    if (collaboration) {
      const officeId = type === 'agency' ? collaboration.agency?.id : collaboration.advertiser?.id;
      this.router.navigate([`companies`, 'office', officeId]);
    }
  }
}
